/**
 * Turns simulation state into sound cues. `afterTick()` runs after every
 * simulation tick and infers jumps, landings and reactive-surface contact from
 * state transitions (like the developer event log); `frame()` runs once per
 * rendered frame and drives the rushing-air layer from speed and how close
 * geometry is. It only reads the simulation: nothing here changes movement.
 */
import { Vec3 } from '../core/Vec3';
import type { ProximityContact } from '../simulation/CollisionQueries';
import type { Simulation } from '../simulation/Simulation';
import type { AudioEngine } from './AudioEngine';

/** Falling faster than this on landing is an impact rather than a landing, m/s. */
export const IMPACT_FALL_SPEED = 14;
/** Below this fall speed a landing is silent (stepping off a kerb), m/s. */
const SILENT_FALL_SPEED = 1.2;
/** Rushing air starts at this planar speed and is full at WIND_FULL. */
const WIND_START = 7;
const WIND_FULL = 24;
/** Geometry within this distance makes the air louder (narrow corridors), m. */
const PROXIMITY_RANGE = 1.6;
const PROXIMITY_EVERY_MS = 100;

export function windLevel(speed: number, proximity: number): number {
  const t = Math.max(0, Math.min(1, (speed - WIND_START) / (WIND_FULL - WIND_START)));
  return t * t * (3 - 2 * t) * (1 + 0.6 * proximity);
}

export class MovementSounds {
  private prevGrounded: boolean;
  private prevVy = 0;
  private prevReactive = false;
  private proximity = 0;
  private lastProbe = -Infinity;
  private readonly probe = new Vec3();
  private readonly contacts: ProximityContact[] = [];
  private readonly unsubscribe: (() => void)[];
  speedSound = true;

  constructor(
    private readonly sim: Simulation,
    private readonly audio: AudioEngine,
  ) {
    this.prevGrounded = sim.state.grounded;
    this.unsubscribe = [
      sim.events.on('death', () => audio.play('death')),
      sim.events.on('checkpoint', () => audio.play('checkpoint')),
      sim.events.on('finish', (e) => audio.play(e.valid || e.mode === 'practice' ? 'finish' : 'invalid')),
      sim.events.on('attempt-invalidated', () => audio.play('invalid', 0.6)),
      sim.events.on('respawn', () => {
        this.prevGrounded = sim.state.grounded;
        this.prevVy = 0;
      }),
    ];
  }

  afterTick(): void {
    const s = this.sim.state;
    if (!s.alive) return;
    if (s.ticksSinceJump === 1) this.audio.play('jump');
    else if (!this.prevGrounded && s.grounded) {
      const fall = -this.prevVy;
      if (fall >= IMPACT_FALL_SPEED) this.audio.play('impact', Math.min(1, fall / 30));
      else if (fall >= SILENT_FALL_SPEED) this.audio.play('land', Math.min(1, 0.3 + fall / IMPACT_FALL_SPEED));
    }
    const reactive = s.grounded && s.groundMaterial?.kind === 'reactive';
    if (reactive && !this.prevReactive) this.audio.play('reactive');
    this.prevReactive = reactive;
    this.prevGrounded = s.grounded;
    this.prevVy = s.velocity.y;
  }

  frame(nowMs: number, paused: boolean): void {
    if (paused || !this.speedSound || !this.sim.state.alive) {
      this.audio.quiet();
      return;
    }
    const s = this.sim.state;
    const speed = s.planarSpeed();
    if (speed > WIND_START && nowMs - this.lastProbe > PROXIMITY_EVERY_MS) {
      this.lastProbe = nowMs;
      this.proximity = this.measureProximity();
    } else if (speed <= WIND_START) this.proximity = 0;
    const level = windLevel(speed, this.proximity);
    this.audio.setWind(level, Math.min(1, speed / WIND_FULL));
  }

  dispose(): void {
    for (const u of this.unsubscribe) u();
    this.audio.quiet();
  }

  /** 0 (open space) .. 1 (geometry touching the capsule), from the nearest non-floor surface. */
  private measureProximity(): number {
    const s = this.sim.state;
    this.probe.copy(s.position);
    const n = this.sim.motor.resolver.nearby(this.probe, s.up, PROXIMITY_RANGE, this.contacts);
    let nearest = PROXIMITY_RANGE;
    for (let i = 0; i < n; i++) {
      const c = this.contacts[i]!;
      // Ignore the floor under the player: only walls and ceilings make a corridor.
      if (c.normal.y > 0.7) continue;
      nearest = Math.min(nearest, Math.max(0, c.distance));
    }
    return 1 - nearest / PROXIMITY_RANGE;
  }
}
